export type CallDetailStateCardTone = 'ready' | 'waiting' | 'muted';

export type CallDetailStateCardKey =
  | 'recording'
  | 'processing'
  | 'transcript'
  | 'coaching';

export interface CallDetailStateInput {
  hasRecording: boolean;
  hasRecordingMetadata: boolean;
  hasEngineSummary: boolean;
  hasTranscript: boolean;
  hasCoaching: boolean;
}

export interface CallDetailStateCard {
  key: CallDetailStateCardKey;
  label: string;
  tone: CallDetailStateCardTone;
  detail: string;
}

export function buildCallDetailStateCards(input: CallDetailStateInput): CallDetailStateCard[] {
  const recordingCard: CallDetailStateCard = !input.hasRecording
    ? {
        key: 'recording',
        label: 'No recording captured',
        tone: 'muted',
        detail: 'This call only has server call metadata.',
      }
    : input.hasRecordingMetadata
      ? {
          key: 'recording',
          label: 'Recording captured',
          tone: 'ready',
          detail: 'Twilio recording metadata is available for review.',
        }
      : {
          key: 'recording',
          label: 'Recording expected',
          tone: 'waiting',
          detail: 'The call says a recording exists, but Twilio metadata has not arrived yet.',
        };

  const processingCard: CallDetailStateCard = !input.hasRecording
    ? {
        key: 'processing',
        label: 'Engine summary not applicable',
        tone: 'muted',
        detail: 'There is no recording/transcript source for the Local Engine to summarize.',
      }
    : !input.hasRecordingMetadata
      ? {
          key: 'processing',
          label: 'Waiting for recording metadata',
          tone: 'waiting',
          detail: 'Engine processing starts after the recording/transcript source is available to the server.',
        }
      : input.hasEngineSummary
        ? {
            key: 'processing',
            label: 'Engine summary available',
            tone: 'ready',
            detail: 'The Local Engine returned safe summary context for this call.',
          }
        : {
            key: 'processing',
            label: 'Engine summary pending',
            tone: 'waiting',
            detail: 'Summary status is based on completed Local Engine output.',
          };

  return [
    recordingCard,
    processingCard,
    input.hasTranscript
      ? {
          key: 'transcript',
          label: 'Transcript available',
          tone: 'ready',
          detail: 'Open the transcript panel to lazy-load the server-owned transcript.',
        }
      : {
          key: 'transcript',
          label: 'No transcript uploaded',
          tone: 'muted',
          detail: 'The server has no transcript row for this call yet.',
        },
    input.hasCoaching
      ? {
          key: 'coaching',
          label: 'Coaching notes available',
          tone: 'ready',
          detail: 'Coaching notes are rendered below from the server-owned coaching row.',
        }
      : {
          key: 'coaching',
          label: 'No coaching notes',
          tone: 'muted',
          detail: 'Coaching appears only when coaching notes exist for this call.',
        },
  ];
}
